import { useState, useMemo, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import axios from '../../api/axios';
import SearchIcon from '@mui/icons-material/Search';
import ContentLayout from '../ContentLayout/ContentLayout';
import { ArchivedPlansBuilders } from './ArchivedPlansBuilders';

const CREATE_PLAN = "/plan/";

export const ArchivePlans = () => {

  const [buildersList, setBuildersList] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    axios.post(CREATE_PLAN, {
      token: localStorage.getItem('token'),
      returnListOfPlans: 'true'
    })
    .then((response) => {
      const plansList = response.data?.message?.listOfPlans || []
      setBuildersList(transformData(plansList));
    })
    .catch((error) => {
      console.error('Error fetching build stages:', error);
    }).finally(() => {
      setLoading(false)
    })
    
  }, []);

  const naturalSort = (a, b) => {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  };

  const transformData = (plansList) => {
    if (!plansList) return [];

    // First create a map to group by builder
    const builderMap = new Map();

    plansList.forEach(plan => {
      if (!plan.archived) {
        return;
      }
      const builderId = plan.builder?.id || "unknown";
      const builderName = plan.builder?.builderName || "Unknown Builder";
      const cadFilelastModifiedDate = plan.cadFiles?.fileDateTime ? new Date(plan.cadFiles.fileDateTime) : new Date(0);
      const cadFileUploadedDate = plan.cadFiles?.uploadedDateAndTime ? new Date(plan.cadFiles.uploadedDateAndTime) : new Date(0);
      const archived = plan.builder?.archived || false;

      // Get or create the builder entry
      if (!builderMap.has(builderId)) {
        builderMap.set(builderId, {
          id: builderId,
          name: builderName,
          plans: [],
          archived: archived
        });
      }

      // Format the plan data with additional sorting fields
      const formattedPlan = {
        id: plan.id || "",
        project: plan.project?.projectName || "No Project Assigned",
        projectSortKey: plan.project?.projectName || "", // For natural sorting
        projectArchived: plan.project?.archived || false,
        lot: plan.lot_Name,
        lotSpecific: plan.lot_Specific,
        planNote: plan.plan_Note,
        nonstandardAnchor: plan.nonstandard_Anchor,
        anchorNote: plan.anchor_Note,
        planNumber: plan.planNumber?.planNumberName || "",
        elevation: plan.elevation?.elevationName || "",
        layout: plan.layout || "",
        options: plan.planOptions || [],
        buildStages: plan.buildStages || [],
        lastModifiedCadFileDateTime: cadFilelastModifiedDate,
        uploadedCadFileDateTime: cadFileUploadedDate,
        uploadedCadFileBy: plan.cadFiles?.uploadedBy,
        fileName: plan.cadFiles?.fileName,
        fileStatus: plan.cadFiles?.fileStatus || "processing",
        lastModifiedTimestamp: cadFilelastModifiedDate.getTime(),
        archived: plan.archived,
        archivedBy: plan.archivedBy || "",
        archivedOn: plan.archivedOn || "",
        archivingUser: plan?.archivingUser || null,
        // Add numeric values for sorting
        planNumberNum: parseInt(plan.planNumber?.planNumberName?.replace(/\D/g, "") || "0", 10),
        elevationNum: parseInt(plan.elevation?.elevationName?.replace(/\D/g, "") || "0", 10)
      };
      builderMap.get(builderId).plans.push(formattedPlan);
    });

    return Array.from(builderMap.values())
      .map(builder => ({
        ...builder,
        plans: [...builder.plans].sort((a, b) => {
          // 1. Sort by project name (natural)
          const projectCompare = naturalSort(a.projectSortKey, b.projectSortKey);
          if (projectCompare !== 0) return projectCompare;

          // 2. Sort by plan number (natural, including the text)
          const planNumberCompare = naturalSort(a.planNumber, b.planNumber);
          if (planNumberCompare !== 0) return planNumberCompare;

          // 3. Sort by elevation (natural)
          const elevationCompare = naturalSort(a.elevation, b.elevation);
          if (elevationCompare !== 0) return elevationCompare;

          // 4. Sort by last modified timestamp (newest first)
          return b.lastModifiedTimestamp - a.lastModifiedTimestamp;
        })
      }))
      .filter(builder => builder.plans.length > 0)
      // Sort builders alphabetically as well
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const [expandedBuildersInBuiAndProExp, setExpandedBuildersInBuiAndProExp] = useState(() => {
    const saved = localStorage.getItem('expandedBuildersInBuiAndProExpArchived');
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedProjects, setExpandedProjects] = useState(() => {
    const saved = localStorage.getItem('expandedProjectsArchived');
    return saved ? JSON.parse(saved) : {};
  });

  // Handlers for toggling expansion in BuildersAndProjectsExpandable.js
  const handleBuilderInBuiAndProExpToggle = (builderId, isExpanded) => {
    const newState = {
      ...expandedBuildersInBuiAndProExp,
      [builderId]: isExpanded
    };
    setExpandedBuildersInBuiAndProExp(newState);
    localStorage.setItem('expandedBuildersInBuiAndProExpArchived', JSON.stringify(newState));
  };

  const handleProjectToggle = (projectId, isExpanded) => {
    const newState = {
      ...expandedProjects,
      [projectId]: isExpanded
    };
    setExpandedProjects(newState);
    localStorage.setItem('expandedProjectsArchived', JSON.stringify(newState));
  };

  const [searchTerm, setSearchTerm] = useState('');

  const filterBuilders = (data, term) => {
    if (!term.trim()) return data;

    const terms = term.toLowerCase().split(/\s+/).filter(t => t);

    return data
      .map(builder => {
        // Filter plans to those that match ALL terms
        const filteredPlans = builder.plans.filter(plan =>
          terms.every(term =>
            plan.planNumber?.toLowerCase().includes(term) ||
            plan.project?.toLowerCase().includes(term) ||
            builder.name?.toLowerCase().includes(term) ||  // Include builder name
            plan.lot?.toLowerCase().includes(term) ||
            plan.elevation?.toLowerCase().includes(term) ||
            plan.fileName?.toLowerCase().includes(term) ||
            (plan.options || []).some(option =>
              option.name?.toLowerCase().includes(term) ||
              option.planOptionName?.toLowerCase().includes(term)
            ) ||
            (plan.buildStages || []).some(stage =>
              stage.name?.toLowerCase().includes(term) ||
              stage.buildStageName?.toLowerCase().includes(term)
            )
          )
        );

        // Only include builders with matching plans
        if (filteredPlans.length > 0) {
          return { ...builder, plans: filteredPlans };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleRestorePlan = async (planId) => {
      try {
          if (!planId) {
              console.error("No plan selected to restore.");
              return;
          }

          const res = await axios.post(CREATE_PLAN, {
              token: localStorage.getItem("token"),
              planId,
              archivePlan: false,
          });

          const { code, message } = res.data;

          if (code === 0) {
              const updatedBuilders = buildersList.map(builder => ({
                ...builder,
                plans: builder.plans.filter(plan => plan.id !== planId)
              }));
              setBuildersList(updatedBuilders);
          } else {
              console.error("Archiving failed:", message);
          }
      } catch (err) {
          console.error("Error archiving plan:", err);
      }
  };

  const filteredBuilders = useMemo(
    () => filterBuilders(buildersList || [], searchTerm),
    [buildersList, searchTerm]
  );

  return (
    <ContentLayout title={"Archived Plans"} url={"/plans"}>

      {loading ? (
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            minHeight: "400px"
          }}
        >
          <CircularProgress/>
        </Box>
      ) : (
        buildersList.length === 0 ? (

          <div style={{ textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              There are no archived plans available.
            </Typography>
          </div>

        ) : (
          <>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              flexWrap: 'wrap'
            }}>
              <TextField
                placeholder="Find a plan..."
                variant="standard"
                size="small"
                sx={{ ml: 'auto' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: { width: 250 }
                }}
              />
            </Box>

            {searchTerm && filteredBuilders.length === 0 ? <Typography variant="h4" sx={{ textAlign: "center" }}>
              No matching results
            </Typography> :
              <ArchivedPlansBuilders builders={filteredBuilders} searchTerm={searchTerm}
                expandedProjects={expandedProjects} onProjectToggle={handleProjectToggle}
                expandedBuilders={expandedBuildersInBuiAndProExp} onBuilderToggle={handleBuilderInBuiAndProExpToggle} 
                handleRestorePlan={handleRestorePlan}/>
            }
          </>
        )
      )
      }
    </ContentLayout>
  );
};